import React from "react";
import clsx from "clsx";
import moment from "moment";
import SofIcon from "../common/Icons";
import { ActivityLog } from "../../models/ProcessFlowConfig";
import { connect } from "react-redux";
import { DefaultButton } from "@fluentui/react";
import { ErrorResponse } from "../../models";
import { FontIcon } from "@fluentui/react";
import { getTheme } from "@fluentui/react";
import { IconButton } from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/react";
import { MessageBar } from "@fluentui/react";
import { MessageBarType } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react";
import { ProcessFlowStep } from "../../models/ProcessFlowConfig";
import { Post } from "../../api/apiUtils";
import { State } from "../../store";
import { useEffect } from "react";
import { useState } from "react";
import { ISofProps } from "./TruckSofPage";
import EditTimestampItem from "./EditTimestampItem";
import StepPreviewPanel from "./StepPreviewPanel";

interface Props extends ISofProps {
  step: ProcessFlowStep | undefined;
  surveyor?: string;
  readonly: boolean;
  onClose(): void;
}

interface ActivityTimestamp {
  activityLogId: number;
  timestamp: string;
  isError: boolean;
  status: number;
}

const classes = mergeStyleSets({
  container: {
    padding: 35,
    width: 520,
  },
  header: {
    display: "flex",
    flex: "1 1 auto",
  },
  collapsibleHeader: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    padding: "8px 4px",
    marginTop: 12,
    borderTop: "1px solid #E2E6E8",
    fontWeight: "bolder",
    textTransform: "capitalize",
  },
  body: {
    maxHeight: "400px",
    overflow: "auto",
    overflowX: "hidden",
    padding: 4,
  },
  footer: {
    marginTop: 35,
    display: "flex",
    direction: "rtl",
    justifyContent: "space-between",
  },
  title: {
    fontWeight: 500,
    fontSize: 20,
    lineHeight: 23,
    alignItems: "center",
    letterSpacing: "0.0015em",
      color: "#212529",
  },
  buttons: {
    borderRadius: 2,
    height: 52,
    width: 210,
  },
  inputField: {
    fontSize: "22px !important",
    textAlign: "left",
  },
  calendar: {
    fontSize: 15,
  },
  inputValue: {
    display: "flex",
    justifyContent: "space-between",
    border: "1px solid #E2E6E8",
    backgroundColor: "#FAFAFA",
    padding: 6,
    marginBottom: 4,
  },
});
const theme = getTheme();
const iconButtonStyles = {
  root: {
    color: theme.palette.neutralPrimary,
    marginLeft: "auto",
    marginTop: "4px",
    marginRight: "2px",
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

const TimeStampModal: React.FC<Props> = (props: Props) => {
  const { activityLogs, step, correlationId } = props;
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [description, setDescription] = useState("");
  const [isApproved, setIsApproved] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [groups, setGroups] = useState<ActivityLog[][]>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [nominationNo, setNominationNo] = useState<string>();
  const [currentActivities, setCurrentActivites] = useState<ActivityLog[]>([]);
  const [updatedTimestamps, setUpdatedTimestamps] = useState<
    ActivityTimestamp[]
  >([]);

  const saveTimestampsAsync = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        correlationId,
        timestamps: updatedTimestamps,
      };

      const response = await Post(
        `/api/v1.0/nomination/${nominationNo}/activities/timestamps`,
        JSON.stringify(payload)
      );
      setIsLoading(false);

      if (response.ok) {
        setIsSuccess(true);
        const result = (await response.json()) as ActivityLog[];
        props.insertActivityLogs(result);
      } else {
        const result = (await response.json()) as ErrorResponse;
        const message = JSON.parse(result.message);

        setErrorMessage(
          `${message.field}${moment
            .utc(`${message.date} UTC`)
            .local()
            .format("ll HH:mm")}`
        );
      }
    } catch (error) {
      setErrorMessage("You are currently offline. Unable to save changes. ");
      setIsLoading(false);

      return false;
    }
  };

  const onChange = (activityLogId: number, value: Date): string | null => {
    if (activityLogs.result) {
      const activityLog = activityLogs.result.find(
        (f) => f.id === activityLogId
      );
      console.log("Current act: ", activityLog);
      if (activityLog === undefined) return null;
      const statusIds = [1, 2, 5];
      const stepActivities = activityLogs.result.filter(
        (f) =>
          f.processStepId === activityLog.processStepId &&
          statusIds.indexOf(f.status) >= 0
      );
      const index = stepActivities.findIndex((p) => p.id === activityLogId);
      if (index > 0) {
        const previous = stepActivities[index - 1];
        console.log("Previous act: ", previous);
        const editedPrevious = updatedTimestamps.find(
          (f) => f.activityLogId === previous.id
        );
        const previousTime =
          editedPrevious !== undefined
            ? new Date(editedPrevious.timestamp)
            : moment.utc(previous.logTime).toDate();
        const preStatus = getStatus(
          editedPrevious !== undefined
            ? editedPrevious.status
            : previous.status,
          previous.changes !== undefined &&
            previous.changes.indexOf("paused") >= 0
        );
        if (previousTime > value) {
          addItem({ activityLogId, isError: true });
          return `Must not be earlier than ${preStatus} timestamp.`;
        }
      }

      const next =
        stepActivities.length > index + 1
          ? stepActivities[index + 1]
          : undefined;
      if (next) {
        const editedNext = updatedTimestamps.find(
          (f) => f.activityLogId === next.id
        );
        const nextTime =
          editedNext !== undefined
            ? new Date(editedNext.timestamp)
            : moment.utc(next.logTime).toDate();
        const nextStatus = getStatus(
          editedNext !== undefined ? editedNext.status : next.status,
          activityLog.status === 5
        );
        if (nextTime < value) {
          addItem({ activityLogId, isError: true });
          return `Must not be later than ${nextStatus} timestamp.`;
        }
      }
      addItem({
        activityLogId,
        timestamp: value.toISOString(),
        isError: false,
        status: activityLog.status,
      });
    }

    return null;
  };

  const getStatus = (status: number, isResume: boolean = false): string => {
    if (status === 0) {
      return "creation";
    } else if (status === 1) {
      if (isResume) return "resume";
      return "start";
    } else if (status === 2) {
      return "end";
    } else if (status === 3) {
      return "void";
    } else if (status === 4) {
      return "skip";
    } else if (status === 5) {
      return "pause";
    }
    return "";
  };

  const addItem = (item: any) => {
    setUpdatedTimestamps([
      ...updatedTimestamps.filter(
        (f) => f.activityLogId !== item.activityLogId
      ),
      item,
    ]);
  };

  useEffect(() => {
    setHasError(updatedTimestamps.filter((f) => f.isError).length > 0);
  }, [updatedTimestamps]);

  useEffect(() => {
    if (activityLogs.result && step) {
      const stepActivityLogs = activityLogs.result.filter(
        (f) =>
          f.processStepId === step.id &&
          (props.truckNomination.result === undefined ||
            f.nominationNo === props.truckNomination.result.nominationNo) &&
          f.surveyor === props.surveyor
      );

      const maxRepeationOrder = Math.max.apply(
        null,
        stepActivityLogs.map((s) => s.repetitionOrder)
      );
      const activeActivities = stepActivityLogs.filter(
        (f) => f.repetitionOrder === maxRepeationOrder
      );
      const editableStatus = [1, 2, 5];
      setCurrentActivites(
        activeActivities.filter((f) => editableStatus.indexOf(f.status) >= 0)
      );

      let _groups = [];
      for (var ctr = maxRepeationOrder - 1; ctr > 0; ctr--) {
        const g = stepActivityLogs
          .filter((f) => f.repetitionOrder === ctr)
          .map((f) => f);
        _groups.push(g);
      }
      setGroups(_groups);

      for (var ctr = 0; ctr < activeActivities.length; ctr++) {
        const activeActivity = activeActivities[ctr];

        if (ctr === activeActivities.length - 1) {
          setDescription(activeActivity.description);
          setNominationNo(activeActivity.nominationNo);
        }
      }
    }

    if (props.truckNomination.result) {
      setIsApproved(props.truckNomination.result.status === 4);
    }
  }, []);

  return (
    <div className="modal-bg">
      <div className={"modal-right"}>
        {!isSuccess && (
          <form onSubmit={saveTimestampsAsync}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span className={classes.title}>Activity Details</span>
              <button className="btn-close-action" onClick={props.onClose}>
                <FontIcon iconName="cancel" />
              </button>
            </div>

            <div className={clsx(classes.body, "custom-scroll")}>
              <div className="ms-Grid" dir="ltr">
                <div className="ms-Grid-row" style={{ marginBottom: "10px" }}>
                  <div className="ms-Grid-col ms-sm12">
                    <div
                      className={classes.collapsibleHeader}
                      onClick={() => setShowDetails(!showDetails)}
                    >
                      <span>{description}</span>
                      {showDetails && <FontIcon iconName="ChevronUp" />}
                      {!showDetails && <FontIcon iconName="ChevronDown" />}
                    </div>
                  </div>
                </div>

                {showDetails && (
                  <>
                    <div className="ms-Grid-row">
                      <div className="ms-Grid-col ms-sm12">
                        {errorMessage && (
                          <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            onDismiss={() => setErrorMessage(undefined)}
                            dismissButtonAriaLabel="Close"
                          >
                            {errorMessage}
                          </MessageBar>
                        )}
                      </div>
                    </div>

                    {currentActivities.map((activity) => (
                      <EditTimestampItem
                        key={activity.id}
                        activityLog={activity}
                        disabled={isApproved || isLoading}
                        onChange={onChange}
                      />
                    ))}
                  </>
                )}

                {groups &&
                  groups.map((item) => (
                    <StepPreviewPanel activityLogs={item} />
                  ))}
              </div>
            </div>

            <div className="ms-Grid" dir="ltr">
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <div className={classes.footer}>
                    <DefaultButton
                      className={classes.buttons}
                      onClick={props.onClose}
                      disabled={isLoading}
                    >
                      {props.readonly ? "Close" : "Cancel"}
                    </DefaultButton>

                    {!props.readonly && (
                      <PrimaryButton
                        className={classes.buttons}
                        disabled={
                          hasError ||
                          isApproved ||
                          isLoading ||
                          updatedTimestamps.length === 0
                        }
                        type="submit"
                      >
                        Confirm
                      </PrimaryButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
        {isSuccess && (
          <>
            <div className={classes.header}>
              <IconButton
                styles={iconButtonStyles}
                iconProps={{ iconName: "Cancel" }}
                ariaLabel="Close popup modal"
                onClick={props.onClose}
                disabled={isLoading}
              />
            </div>
            <div className="ms-Grid" dir="ltr" style={{ marginBottom: 40 }}>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <SofIcon iconname="Success" />
                </div>
              </div>

              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <div className={classes.title}>{description}</div>
                  <div className={classes.title}>Timestamp updated</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default connect((state: State) => state.activityLogs)(TimeStampModal);
